import React from 'react';
import { CSSObject, forBrands, Theme } from '@core-ui/react-stitch';
import { SizeButtonProps } from '../../types';
import { getSizeButtonStyleForBananaRepublic } from '../Style/BRRedesignStyles';

const SizeButton = ({
  name,
  style,
  applied,
  description,
  variant,
  onClick,
}: SizeButtonProps) => {
  const getSizeButtonStyleForGap = (t: Theme) => ({
    lineHeight: '0.9',
    backgroundColor: applied ? t.color.b1 : t.color.wh,
    color: applied ? t.color.wh : t.color.b2,
    borderRadius: 25,
  });

  const getBackgroundColor = (applied: boolean, t: Theme) => {
    if (applied) {
      return t.color.b1;
    }
    return t.color.wh;
  };

  const getColor = (applied: boolean, t: Theme) => {
    if (applied) {
      return t.color.wh;
    }
    return t.color.b1;
  };

  const getSizeButtonStyleForOldNavy = (t: Theme): CSSObject => ({
    lineHeight: '1.05',
    backgroundColor: getBackgroundColor(applied, t),
    color: getColor(applied, t),
    fontSize: '0.75rem',
    borderRadius: applied ? '24px' : 'inherit',
  });

  const getSizeButtonStyleForAthleta = (t: Theme): CSSObject => ({
    lineHeight: '0.9',
    backgroundColor: applied ? t.color.g6 : t.color.wh,
    color: t.color.b1,
    borderRadius: '20px',
  });

  const getHoverStyle = (t: Theme): CSSObject => ({
    ...(forBrands(t, {
      default: () => ({
        borderRadius: 25,
      }),
      br: () => ({
        borderRadius: 0,
      }),
      brfs: () => ({
        borderRadius: 0,
      }),
    }) as CSSObject),
  });

  const setSizeButtonColorsForBananaRepublic = (t: Theme) => ({
    backgroundColor: t.color.wh,
    // color: t.color.b2,
    color: '#000000',
  });

  const sizeButtonStyle = (theme: Theme): CSSObject => ({
    '&:hover': {
      backgroundColor: theme.color.gray10,
      color: theme.color.b1,
      ...(forBrands(theme, {
        br: setSizeButtonColorsForBananaRepublic,
        brfs: setSizeButtonColorsForBananaRepublic,
      }) as CSSObject),
      ...getHoverStyle(theme),
    },
    ...theme.brandFont,
    appearance: 'none',
    minWidth: '2.5rem',
    overflow: 'visible',
    padding: '0.5rem',
    position: 'relative',
    verticalAlign: 'middle',
    border: 'none',
    boxSizing: 'border-box',
    height: '2.5rem',
    fontSize: applied ? '0.9375rem' : 'inherit',
    fontWeight: applied ? 'bold' : 'normal',
    textTransform: 'uppercase',
    ...(forBrands(theme, {
      gap: getSizeButtonStyleForGap,
      gapfs: getSizeButtonStyleForGap,
      on: getSizeButtonStyleForOldNavy,
      at: getSizeButtonStyleForAthleta,
      br: () => getSizeButtonStyleForBananaRepublic(theme, applied),
      brfs: () => getSizeButtonStyleForBananaRepublic(theme, applied),
    }) as CSSObject),
  });

  const setSizeButtonDescriptionFontSizeForBananaRepublic = (): CSSObject => ({
    fontSize: '0.75rem',
  });

  const sizeButtonDescriptionStyle = (theme: Theme): CSSObject => ({
    paddingTop: '0.1rem',
    paddingBottom: '0.1rem',
    display: 'block',
    fontWeight: 'normal',
    fontSize: '0.8rem',
    ...(forBrands(theme, {
      br: setSizeButtonDescriptionFontSizeForBananaRepublic,
      brfs: setSizeButtonDescriptionFontSizeForBananaRepublic,
    }) as CSSObject),
  });

  return (
    <button
      key={`${style}-${name}`}
      aria-checked={applied}
      aria-label={
        description
          ? `${variant} ${style} size ${name} ${description}`
          : `${variant} ${style} size ${name}`
      }
      css={sizeButtonStyle}
      onClick={onClick}
      role="checkbox"
      type="button"
    >
      {name}
      {description && (
        <span css={sizeButtonDescriptionStyle}>{description}</span>
      )}
    </button>
  );
};

export default SizeButton;
